/**
 * NT-HOTFIX-01 (H7) — Render-Pause für Hintergrund-Aktualisierungen.
 *
 * Die Tab-Views aktualisieren sich periodisch (Polling) per vollem innerHTML-Replace. Ein offenes
 * Dropdown, ein Eingabefeld mit Fokus oder ein offenes Modal wurde dadurch alle 30–60 s
 * geschlossen bzw. zurückgesetzt. Diese Datei ist die EINZIGE Stelle, die entscheidet, ob ein
 * Hintergrund-Render gerade stören würde: dann wird er zurückgestellt und nachgeholt, sobald die
 * Interaktion vorbei ist.
 *
 * Nur für Renders aus Polling/Timern verwenden — Renders als direkte Folge einer Spieler-Aktion
 * (Klick auf +/−, Modal öffnen, Absenden) laufen weiterhin sofort.
 */

const RETRY_INTERVAL_MS = 500;

interface PendingRender {
  rootId: string;
  render: () => void;
}

const pending = new Map<string, PendingRender>();
let retryTimer: ReturnType<typeof setInterval> | null = null;

/** Arbeitet der Spieler gerade in dieser View (fokussiertes Formularfeld oder offenes Modal)? */
export function isUserInteracting(root: HTMLElement): boolean {
  const active = document.activeElement;
  if (
    (active instanceof HTMLSelectElement || active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)
    && root.contains(active)
  ) {
    return true;
  }
  return root.querySelector('.modal-backdrop') !== null;
}

function flushPending(): void {
  for (const [key, entry] of pending) {
    const root = document.getElementById(entry.rootId);
    if (!root) { pending.delete(key); continue; } // View wurde verlassen — nichts nachzuholen
    if (isUserInteracting(root)) continue;
    pending.delete(key);
    entry.render();
  }
  if (pending.size === 0 && retryTimer !== null) {
    clearInterval(retryTimer);
    retryTimer = null;
  }
}

/**
 * Führt einen Hintergrund-Render aus — oder stellt ihn zurück, solange der Spieler in der View
 * (Root-Element `rootId`) interagiert. Mehrere zurückgestellte Renders derselben View (`key`)
 * werden zu einem zusammengefasst.
 */
export function renderWhenIdle(key: string, rootId: string, render: () => void): void {
  const root = document.getElementById(rootId);
  if (!root) return;
  if (!isUserInteracting(root)) {
    pending.delete(key);
    render();
    return;
  }
  pending.set(key, { rootId, render });
  retryTimer ??= setInterval(flushPending, RETRY_INTERVAL_MS);
}

/** Verwirft einen zurückgestellten Render (z. B. beim Verlassen der View). */
export function cancelPendingRender(key: string): void {
  pending.delete(key);
}
